import { Link, Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import {
  AlertSchedule,
  DayOfWeekEnum,
  ModelAlertSettings,
  ObjectTypeEnum,
} from "../../../../../API";
import HardHatTypeIcon from "../../../../../common/components/icons/HardHatTypeIcon";
import PeopleTypeIcon from "../../../../../common/components/icons/PeopleTypeIcon";
import VehicleTypeIcon from "../../../../../common/components/icons/VehicleTypeIcon";
import { useTimeZoneFromLocationId } from "../../../../../common/hooks/useTimeZoneFromLocationId";
import { useUnsavedChangesConfirm } from "../../../../../common/hooks/useUnsavedChangesConfirm";
import { RouteEnum } from "../../../../../common/models/enums";
import { useUpdateModelAlertSettings } from "../../../../model-manager/hooks/useUpdateModelAlertSettings";
import { useUpdateService } from "../../../../model-manager/hooks/useUpdateService";
import { ServiceTypeEnum } from "../../../../model-manager/variables/modelManager";
import ModelSettingsParamRow from "./ModelSettingsParamRow";
import TimeZoneInformator from "./TimeZoneInformator";

type ParamForm = {
  objectType: string;
  onSaveLoading: boolean;
  onToggleLoading: boolean;
  enabled: boolean;
  editMode: boolean;
  activeDays: DayOfWeekEnum[];
  schedules?: AlertSchedule[];
};

export type AlertModelSettingsHandle = {
  hasUnsavedChanges: () => boolean;
  save: () => Promise<void>;
};

export type ModelSettingsParamFormValues = {
  parameters: ParamForm[];
};

type ModelAlertSettingsProps = {
  modelAlertSettings: ModelAlertSettings;
  setCloseGuard?: (guard: (doClose: () => void) => void) => void;
  locationId: string;
  nodeId: string;
};

const TYPE_META: Record<
  ObjectTypeEnum,
  { Icon: React.ElementType; label: string; desc: string }
> = {
  [ObjectTypeEnum.PEOPLE]: {
    Icon: PeopleTypeIcon,
    label: ServiceTypeEnum.PEOPLE,
    desc: "Detects human presence and movement in the camera’s field of view.",
  },
  [ObjectTypeEnum.VEHICLE]: {
    Icon: VehicleTypeIcon,
    label: ServiceTypeEnum.VEHICLE,
    desc: "Recognizes cars and trucks near the facility.",
  },
  [ObjectTypeEnum.HARDHAT]: {
    Icon: HardHatTypeIcon,
    label: ServiceTypeEnum.HARD_HAT,
    desc: "Identifies workers not wearing safety helmets in monitored areas.",
  },
};

const AlertModelSettings = forwardRef<
  AlertModelSettingsHandle,
  ModelAlertSettingsProps
>(({ modelAlertSettings, setCloseGuard, locationId, nodeId }) => {
  const theme = useTheme();
  const defaultValues = useMemo<ModelSettingsParamFormValues>(
    () => ({
      parameters: (modelAlertSettings.modelAlertParameters ?? []).map(p => ({
        objectType: String(p.objectType),
        onSaveLoading: false,
        onToggleLoading: false,
        enabled: Boolean(p.enabled),
        editMode: false,
        activeDays: p.activeDays ?? [],
        schedules: p.schedules ?? [],
      })),
    }),
    [modelAlertSettings.modelAlertParameters]
  );

  const methods = useForm<ModelSettingsParamFormValues>({
    defaultValues,
    mode: "onChange",
  });
  const { control, handleSubmit, getValues, formState, watch, reset } = methods;

  const { fields } = useFieldArray({ control, name: "parameters" });
  const { updateModelAlertSettings, loading } = useUpdateModelAlertSettings();
  const { updateService } = useUpdateService();
  const pendingCloseRef = useRef<null | (() => void)>(null);

  const normalizeForBaseline = useCallback(
    (values: ModelSettingsParamFormValues): ModelSettingsParamFormValues => ({
      parameters: values.parameters.map(p => ({
        ...p,
        editMode: false,
        onSaveLoading: false,
        onToggleLoading: false,
      })),
    }),
    []
  );

  const onSubmitAll = useCallback(
    async (data?: ModelSettingsParamFormValues) => {
      const form = data ?? getValues();

      const timeZone = useTimeZoneFromLocationId(locationId);

      const input = {
        id: modelAlertSettings.id,
        serviceId: modelAlertSettings.serviceId,
        timeZone,
        modelAlertParameters: form.parameters.map(p => ({
          activeDays: p.activeDays,
          enabled: p.enabled,
          objectType: p.objectType as ObjectTypeEnum,
          schedules: (p.schedules ?? []).map(s => ({
            dayOfWeek: s.dayOfWeek,
            dailyTimeRange: s.dailyTimeRange.map(t => ({
              startMinutesFromMidnight: t.startMinutesFromMidnight,
              endMinutesFromMidnight: t.endMinutesFromMidnight,
            })),
          })),
        })),
      };

      const res = await updateModelAlertSettings(input);
      const enableHardHat = input.modelAlertParameters[2].enabled;

      await updateService({
        configuration: JSON.stringify({
          // hardcoded docker_image
          docker_image:
            "534256877503.dkr.ecr.us-east-1.amazonaws.com/ccai-security:latest",
          enable_hardhat: enableHardHat,
        }),
        serviceId: modelAlertSettings.serviceId,
        nodeId: nodeId,
      });

      if (res.errors) {
        throw res.errors;
      }

      const nowValues = getValues();
      const normalizedValues = normalizeForBaseline(nowValues);

      reset(normalizedValues, { keepDirty: false, keepValues: true });

      if (pendingCloseRef.current) {
        const doClose = pendingCloseRef.current;

        pendingCloseRef.current = null;

        clearDirty();

        setDirty(false);

        doClose();
      }
    },
    [
      getValues,
      modelAlertSettings.id,
      modelAlertSettings.serviceId,
      updateModelAlertSettings,
      reset,
    ]
  );

  const submitFromChild = useCallback(async () => {
    await handleSubmit(onSubmitAll)();
  }, [handleSubmit, onSubmitAll]);

  const { Confirm, guard, setDirty, clearDirty } = useUnsavedChangesConfirm({
    onSave: submitFromChild,
    onSaveLoading: loading,
    onDiscard: () => reset(defaultValues),
  });

  useEffect(() => {
    setCloseGuard?.(doClose => guard(doClose));
  }, [setCloseGuard, guard]);

  useEffect(() => setDirty(formState.isDirty), [formState.isDirty, setDirty]);

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={handleSubmit(onSubmitAll)} sx={{ p: 3 }}>
        <Box sx={{ mb: "1em" }}>
          <Typography
            variant="body1Regular"
            sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}
          >
            Objects type:
          </Typography>

          <TimeZoneInformator timeZone={modelAlertSettings.timeZone} />
        </Box>

        {fields.map((f, idx) => {
          const meta =
            TYPE_META[
              (f.objectType as unknown as ObjectTypeEnum) ??
                ObjectTypeEnum.PEOPLE
            ];

          const isDisabled = !watch(`parameters.${idx}.enabled`);

          const isHardHat = meta.label === ServiceTypeEnum.HARD_HAT;

          const tooltip = (
            <span>
              Events for this object type will appear on
              {` ${isHardHat ? meta.label : "People and Vehicle"} `}
              page, and email alerts will be sent if enabled in{" "}
              <Link
                href={`${RouteEnum.CustomerSettings}/${RouteEnum.AlertManagement}`}
                target="_blank"
              >
                Alert Management.
              </Link>
            </span>
          );

          return (
            <ModelSettingsParamRow
              key={f.id}
              isDisabled={isDisabled}
              index={idx}
              meta={meta}
              tooltip={tooltip}
              onSubmitAll={submitFromChild}
            />
          );
        })}
      </Box>

      {Confirm}
    </FormProvider>
  );
});

export default React.memo(AlertModelSettings);
